import { fetchAuthSession } from 'aws-amplify/auth';

class MCPService {
  constructor() {
    this.sessions = new Map();
    this.toolsByGateway = new Map();
    this.nextId = 1;
  }

  async getAccessToken() {
    const session = await fetchAuthSession();
    return session.tokens.accessToken.toString();
  }

  async connect(gatewayUrl) {
    if (this.sessions.has(gatewayUrl)) {
      return this.sessions.get(gatewayUrl);
    }

    try {
      const accessToken = await this.getAccessToken();

      const response = await fetch(gatewayUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`
        },
        body: JSON.stringify({
          jsonrpc: '2.0',
          id: this.nextId++,
          method: 'initialize',
          params: {
            protocolVersion: '2024-11-05',
            capabilities: {},
            clientInfo: {
              name: 'chulas-assistant',
              version: '1.0.0'
            }
          }
        })
      });

      const data = await response.json();

      if (data.error) {
        throw new Error(`MCP initialization failed: ${data.error.message}`);
      }

      this.sessions.set(gatewayUrl, {
        connected: true,
        serverInfo: data.result?.serverInfo
      });

      await this.listTools(gatewayUrl);

      return this.sessions.get(gatewayUrl);
    } catch (error) {
      console.error('Failed to connect to MCP gateway:', error);
      throw error;
    }
  }

  async listTools(gatewayUrl) {
    if (!this.sessions.has(gatewayUrl)) {
      throw new Error('Not connected to gateway');
    }

    try {
      const accessToken = await this.getAccessToken();

      const response = await fetch(gatewayUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`
        },
        body: JSON.stringify({
          jsonrpc: '2.0',
          id: this.nextId++,
          method: 'tools/list',
          params: {}
        })
      });

      const data = await response.json();

      if (data.error) {
        throw new Error(`Failed to list tools: ${data.error.message}`);
      }

      this.toolsByGateway.set(gatewayUrl, data.result?.tools || []);
      return data.result?.tools || [];
    } catch (error) {
      console.error('Failed to list tools:', error);
      throw error;
    }
  }

  async callTool(gatewayUrl, toolName, args) {
    if (!this.sessions.has(gatewayUrl)) {
      throw new Error('Not connected to gateway');
    }

    try {
      const accessToken = await this.getAccessToken();

      const response = await fetch(gatewayUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`
        },
        body: JSON.stringify({
          jsonrpc: '2.0',
          id: this.nextId++,
          method: 'tools/call',
          params: {
            name: toolName,
            arguments: args || {}
          }
        })
      });

      const data = await response.json();

      if (data.error) {
        throw new Error(`Tool execution failed: ${data.error.message}`);
      }

      return data.result;
    } catch (error) {
      console.error('Failed to call tool:', error);
      throw error;
    }
  }

  getAllTools() {
    const allTools = [];
    for (const tools of this.toolsByGateway.values()) {
      allTools.push(...tools);
    }
    return allTools;
  }

  getBedrockToolSpecs() {
    const tools = this.getAllTools();
    return tools.map(tool => ({
      toolSpec: {
        name: tool.name,
        description: tool.description || '',
        inputSchema: {
          json: tool.inputSchema || {}
        }
      }
    }));
  }

  isConnected(gatewayUrl) {
    return this.sessions.has(gatewayUrl) && this.sessions.get(gatewayUrl).connected;
  }

  disconnect(gatewayUrl) {
    this.sessions.delete(gatewayUrl);
    this.toolsByGateway.delete(gatewayUrl);
  }

  disconnectAll() {
    this.sessions.clear();
    this.toolsByGateway.clear();
  }
}

export default new MCPService();
